import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2,
  User,
  Mail,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  CreditCard,
  Send,
  Rocket,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import toast from 'react-hot-toast';
import { describeApiError } from '../../../utils/apiError';
import { COUNTRIES } from './countries';
import CountryCombobox from './CountryCombobox';
import CommercialConfigurator from './CommercialConfigurator';
import { productLabel } from './productLabels';
import DefaultApplicationSection from './DefaultApplicationSection';
import StatusBadge from '../../../components/admin/StatusBadge';
import IdentityMark from '../../../design-system/identity/IdentityMark';
import {
  DEFAULT_ENVIRONMENT, ENVIRONMENT_ORDER,
  describeEnvironment, sampleCredentialsAllowed,
} from './environmentPresentation';
import { describeDemoConnections, summarizeReadiness } from './sampleConnectionPresentation';
import { SectionCard as Card, DemoConnectionsReview } from './DemoConnectionsReview';
import CreatedTenantSummary from './CreatedTenantSummary';
import ProductSetupOutcome, { setupFailed } from './ProductSetupOutcome';
import { creationInvitationPresentation } from './creationInvitationPresentation';
import ManagedServicesChips from './ManagedServicesChips';
import { MANAGED_SERVICES_NOTE } from './managedServices';
import StarterContentSection from './StarterContentSection';
import {
  chosenStarterModules, defaultStarterContent, describeStarterContentChoice,
  licensedStarterModules, starterContentBlockingReason,
} from './starterContentPresentation';

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_COMMERCIAL = {
  lines: [], bundle_code: null, price_book_code: null, crm_seats: 1,
  product_editions: {}, currency: 'USD', term: 'monthly',
  discount_percent: 0, discount_amount: 0,
};

/** The form as it starts, and as Create Another must restore it. Named because
 *  the reset used to inline a SHORTER object — it dropped `environment`,
 *  `country_code`, `sample_credentials` and both onboarding switches, leaving
 *  controlled inputs bound to `undefined`. */
const EMPTY_FORM = {
  // NO `tenant_name`. The form asked for both a "Tenant Name" and an
  // "Organization Name" and could not say what the difference was, so the same
  // company got typed twice — or twice differently, and the tenant then carried
  // two names that drifted. There is one organisation, so there is one name:
  // the server derives its internal name from `organization_name`, and mints an
  // immutable, collision-safe `tenant_slug` from it for anything that needs an
  // identifier rather than a label. See `TenantCreate.tenant_name`.
  organization_name: '',
  admin_email: '',
  admin_first_name: '',
  admin_last_name: '',
  plan: '',
  industry: '',
  is_trial: false,
  trial_days: 14,
  country_code: '',
  address_line1: '',
  address_line2: '',
  address_city: '',
  address_state: '',
  address_postal_code: '',
  // STARTER CONTENT follows the environment (CRM Foundation decision 9):
  // Development, Demo and Staging start ON; Live starts UNCHOSEN (`null`) and
  // the form cannot be submitted until the operator picks. Independent of
  // demo connections: sample records are rows in the tenant's own database,
  // demo connections are shared provider credentials, and neither implies the
  // other.
  include_sample_data: defaultStarterContent(DEFAULT_ENVIRONMENT),
  // `null` = untouched, which means every licensed product.
  starter_content_modules: null,
  // The invitation defaults OFF — creating a tenant contacts nobody the
  // operator did not ask for.
  send_invitation_when_ready: false,
  // Deployment environment. LIVE is the default here AND in the request
  // model server-side; immutable once the tenant exists.
  environment: DEFAULT_ENVIRONMENT,
  // Shared demo connections. Default OFF; never offered for LIVE, and
  // refused by the backend for LIVE whatever the request says.
  sample_credentials: false,
  docflow_extra_storage_gb: '',
  // Where the tenant's users land. Empty means "the server's default", which is
  // CRM Console landing on CRM Home — deliberately NOT hard-coded here, because
  // a default stated in two places is a default that will one day disagree with
  // itself. `DefaultApplicationSection` fills these in from the server.
  default_app_code: '',
  default_landing_page: '',
};

const INDUSTRIES = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Retail',
  'Manufacturing', 'Real Estate', 'Legal', 'Consulting', 'Other',
];

const CreateTenantPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [, setLoadingPlans] = useState(true);  // the flag had one reader: the removed plan card
  const [creating, setCreating] = useState(false);
  const [success, setSuccess] = useState(null);
  // The product setup retry on the confirmation screen: the server re-runs
  // only the writes that did not land and answers with its read-back.
  const [setupRetrying, setSetupRetrying] = useState(false);
  const [setupRetryError, setSetupRetryError] = useState(null);
  // Code -> product name for the success card, from the same server-built map
  // the selector reads. `module_entitlements` is a list of identifiers
  // (`chatbot`, `live_chat`, `ai_features`); the card prints names.
  const [productLabels, setProductLabels] = useState({});
  // Granted codes that are licensing plumbing, not products (Engage's legacy
  // Form Builder / Survey Builder), from the same presentation feed. The
  // tenant still holds them; the card does not name them as modules.
  const [internalGrants, setInternalGrants] = useState([]);
  // The commercial configuration IS the entitlement decision now — `plan` is a
  // label. Validity is owned by the server: the configurator reports what
  // POST /tenants/commercial/validate said, and this page never second-guesses
  // it with arithmetic of its own.
  // `crm_seats` is stated here, in the field the operator can see, and travels
  // with the rest of the commercial configuration. It is the tenant's enforced
  // CRM user limit and the ceiling for every seat-based product — the server
  // refuses a configuration that does not carry one rather than choosing a
  // number on the operator's behalf.
  // Empty product editions mean each product keeps its catalogue default.
  const [commercial, setCommercial] = useState(EMPTY_COMMERCIAL);
  const [commercialValid, setCommercialValid] = useState(false);
  const [commercialProblems, setCommercialProblems] = useState([]);
  const [docflowSelected, setDocflowSelected] = useState(false);
  const onCommercialValidityChange = useCallback((ok, problems, entitlements) => {
    setCommercialValid(ok);
    setCommercialProblems(problems || []);
    setDocflowSelected((entitlements || []).some((item) => item.app_code === 'docflow'));
  }, []);
  // Whether this deployment can provision a tenant at all. Platform provisioning
  // is the only supported engine, so an unconfigured or unreachable service
  // means Create Tenant cannot succeed — and the operator should learn that
  // before filling in the form rather than after pressing Create.
  const [provisioningReadiness, setProvisioningReadiness] = useState(null);
  // The environment registry: which environments exist and whether each
  // one's Platform runtime is attached on this control plane. Rendered from
  // the server so the screen never carries its own copy of the four.
  const [environments, setEnvironments] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);

  // The demo connection profile for the SELECTED environment, read before the
  // tenant exists. Three fields rather than one, because "loading", "failed"
  // and "answered with nothing" are three different things and a single
  // `profiles || []` would render all three as an empty catalogue.
  const [profilePayload, setProfilePayload] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/admin/environments`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
        );
        setEnvironments(data.environments || []);
      } catch (err) {
        // No registry means no readiness information — rendered as unknown,
        // never as "attached".
        setEnvironments([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!success) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/admin/commercial/presentation`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
        );
        setProductLabels(data?.labels || {});
        setInternalGrants(data?.internal_grants || []);
      } catch (err) {
        // Names fall back to title-cased identifiers; the list is still true.
        setProductLabels({});
      }
    })();
  }, [success]);

  useEffect(() => {
    // Deployment-scoped, so it is asked once on mount, never per keystroke.
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/admin/provisioning/platform-readiness`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } },
        );
        setProvisioningReadiness(data);
      } catch (err) {
        // An unanswerable readiness check is NOT a ready one. Rendered as an
        // explicit unknown, because a missing banner reads exactly like a
        // healthy deployment.
        setProvisioningReadiness({
          state: 'error',
          detail: 'Could not determine whether tenant provisioning is available.',
        });
      }
    })();
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const { data } = await axios.get(`${API}/api/admin/plans`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const activePlans = (data.plans || data || []).filter((p) => p.is_active !== false);
        setPlans(activePlans);
        // DELIBERATELY NO AUTO-SELECTION. This used to seed form.plan with
        // activePlans[0] — whichever plan sorted first, which on the live
        // control plane was a hand-made QA row. Nobody chose it, and it went on
        // to set the tenant's enforced quotas and licence seats. "No plan" is a
        // supported state; entitlements come from the products above.
      } catch (err) {
        console.error('Failed to load plans:', err);
        toast.error('Failed to load subscription plans');
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only; form.plan is intentionally empty on first run
  }, []);

  /**
   * Read the demo connection profile for one environment.
   *
   * Scoped to the environment, not to the tenant — there is no tenant yet.
   * This is the only pre-create read that exists, and it is enough: the
   * Platform's attach does no more than walk this profile's providers, so the
   * screen can state the attach rather than predict it.
   */
  const loadProfiles = useCallback(async (env) => {
    if (!sampleCredentialsAllowed(env)) return;
    setProfileLoading(true);
    setProfileError(null);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/sample-connection-profiles`,
        {
          params: { environment: env },
          headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        },
      );
      setProfilePayload(data);
    } catch (err) {
      // A failed request is NOT an empty catalogue. Clearing the payload and
      // recording the error keeps the two apart all the way to the render.
      setProfileError(describeApiError(err, 'Could not read the demo connection profile'));
      setProfilePayload(null);
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only when it is actually being offered AND asked for — an operator who
    // leaves Demo Connections off should not cause a runtime read at all.
    if (!form.sample_credentials || !sampleCredentialsAllowed(form.environment)) return;
    loadProfiles(form.environment);
  }, [form.sample_credentials, form.environment, loadProfiles]);

  // Whether the operator has made the starter-content choice themselves. Until
  // they have, changing the environment moves the choice to the new
  // environment's default (on for Development/Demo/Staging, unchosen for Live);
  // after, their choice stands.
  const [starterTouched, setStarterTouched] = useState(false);

  const updateField = (field, value) => {
    if (field === 'include_sample_data' || field === 'starter_content_modules') {
      setStarterTouched(true);
    }
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Switching to an environment that cannot carry sample credentials
      // drops the toggle rather than sending a request the server refuses.
      if (field === 'environment' && !sampleCredentialsAllowed(value)) {
        next.sample_credentials = false;
      }
      if (field === 'environment' && !starterTouched) {
        next.include_sample_data = defaultStarterContent(value);
      }
      // When the plan changes to one that does NOT include CRM/Sales
      // Console, clear out the industry value so it isn't accidentally
      // submitted (the field is also hidden in the UI).
      if (field === 'plan') {
        const newPlan = plans.find((p) => p.api_name === value);
        const mods = newPlan?.enabled_modules || [];
        if (!mods.includes('crm') && !mods.includes('sales_console')) {
          next.industry = '';
        }
      }
      return next;
    });
  };


  // The confirmation must report what the server created, not what the form
  // currently holds. Every other row on that screen already reads from
  // `success`; Plan read `selectedPlan` first, so it followed later edits to
  // the form and could name a plan the tenant was not created on.
  const createdPlan = success ? plans.find((p) => p.api_name === success.plan) : null;

  // Everything the Demo Connections section renders, decided in one pure
  // function so the JSX below cannot reach a different conclusion from the
  // tests. The switches are read INDEPENDENTLY: `include_sample_data` is not
  // consulted here and must not be.
  // WHICH PROFILE CREATION WILL RESOLVE — from the server that resolves it.
  //
  // `GET /api/admin/environments` serves `default_sample_profile_id` from
  // `shared.constants.environments.default_sample_profile_id`, and
  // `AdminService.create_tenant` fills an unset `sample_connection_profile_id`
  // by calling that same function for that same environment. Passing it here
  // is what makes the profile REVIEWED and the profile ATTACHED one profile
  // rather than two independent choices — the preflight used to preview the
  // runtime's first entry, which nothing tied to the server's answer.
  //
  // The payload below still sends no `sample_connection_profile_id`, and that
  // is deliberate: the field is trusted rather than validated all the way to
  // `attach_profile_to_tenant`, which checks only that nothing involved is
  // LIVE. This screen has no profile picker, so it has nothing to say that the
  // server does not already know.
  //
  // Unread registry (`null`) is passed through as unknown, never guessed.
  const environmentEntry = (environments || []).find((e) => e.code === form.environment) || null;

  const demo = describeDemoConnections({
    allowed: sampleCredentialsAllowed(form.environment),
    requested: form.sample_credentials,
    loading: profileLoading,
    error: profileError,
    payload: profilePayload,
    environment: form.environment,
    defaultProfileId: environmentEntry?.default_sample_profile_id || null,
  });

  // Which products can carry starter content, from the products being sold.
  // The server filters again against the entitlements it resolves.
  const licensedStarter = licensedStarterModules(commercial);
  const extraStorageValid = !docflowSelected || form.docflow_extra_storage_gb === '' || (
    /^\d+$/.test(String(form.docflow_extra_storage_gb)) &&
    Number(form.docflow_extra_storage_gb) <= 931322
  );

  const canSubmit =
    // Live has no starter-content default: an explicit choice is required.
    starterContentBlockingReason(form) === null &&
    form.organization_name.trim().length >= 2 &&
    form.admin_email.trim() &&
    form.admin_first_name.trim() &&
    form.admin_last_name.trim() &&
    form.country_code &&
    // A tenant may no longer be created on an unvalidated commercial
    // configuration: that is how one ended up existing with entitlements
    // nobody had priced.
    commercialValid && extraStorageValid;

  // WHY THE BUTTON IS DISABLED, in the order the conditions are evaluated.
  //
  // `canSubmit` is six conditions and the button showed none of them. An
  // operator selecting Conversation Center and leaving its capabilities off
  // got a dead button and no sentence — while the server had already answered
  // precisely: "conversation_center is enabled but no capability is switched
  // on, so the tenant would be sold something that does nothing".
  //
  // Returns null when the form CAN submit, so the banner cannot appear beside
  // an enabled button.
  const blockingReason = (() => {
    if (form.organization_name.trim().length < 2) return 'Enter the organisation name.';
    if (!form.country_code) return 'Choose the country.';
    if (!form.admin_first_name.trim()) return "Enter the administrator's first name.";
    if (!form.admin_last_name.trim()) return "Enter the administrator's last name.";
    if (!form.admin_email.trim()) return "Enter the administrator's email address.";
    const starterReason = starterContentBlockingReason(form);
    if (starterReason) return starterReason;
    if (!commercialValid) {
      // The server's own words, not a paraphrase: it knows which line is
      // wrong and a second wording here would drift from it.
      const said = (commercialProblems || [])
        .map((p) => p && p.detail)
        .filter(Boolean);
      return said.length
        ? said.join(' ')
        : 'The product configuration is not valid yet.';
    }
    if (!extraStorageValid) {
      return 'Extra DocFlow storage must be a non-negative whole GB amount.';
    }
    return null;
  })();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setCreating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const countryName = COUNTRIES.find((c) => c.code === form.country_code)?.name || form.country_code;
      const payload = {
        // NO `tenant_name`. The API no longer requires one and the service
        // derives it from the organisation name — sending the same string under
        // two keys would just reintroduce the two names this screen removed.
        organization_name: form.organization_name,
        admin_email: form.admin_email,
        admin_first_name: form.admin_first_name,
        admin_last_name: form.admin_last_name,
        // Omitted when unset, rather than sent as 'free' — a plan named
        // 'free' was how tenants came to point at a document nobody had made.
        ...(form.plan ? { plan: form.plan } : {}),
        commercial,
        ...(docflowSelected && form.docflow_extra_storage_gb !== ''
          ? { docflow_extra_storage_gb: Number(form.docflow_extra_storage_gb) }
          : {}),
        industry: form.industry,
        is_trial: form.is_trial,
        // Sent only when a trial was asked for. The server refuses 0 rather
        // than reading it as "unspecified" and substituting fourteen days.
        ...(form.is_trial ? { trial_days: form.trial_days } : {}),
        country_code: form.country_code,
        // Omitted when unset rather than sent as '' — the server applies its
        // own default (CRM Console / CRM Home) and validates it against the
        // entitlements it just resolved, and an empty string would be a value
        // the operator never chose arriving as though they had.
        ...(form.default_app_code ? { default_app_code: form.default_app_code } : {}),
        ...(form.default_landing_page
          ? { default_landing_page: form.default_landing_page } : {}),
        include_sample_data: form.include_sample_data,
        // Which products, only when starter content is on — never wider than
        // the licence. The server filters once more and seals it in the spec.
        ...(form.include_sample_data
          ? { starter_content_modules: chosenStarterModules(
            form.starter_content_modules, licensedStarter) }
          : {}),
        send_invitation_when_ready: form.send_invitation_when_ready,
        environment: form.environment,
        sample_credentials: sampleCredentialsAllowed(form.environment) && form.sample_credentials,
        company_address: {
          line1: form.address_line1,
          line2: form.address_line2,
          city: form.address_city,
          state: form.address_state,
          postal_code: form.address_postal_code,
          country: countryName,
        },
      };
      const { data } = await axios.post(
        `${API}/api/admin/tenants`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(data);
      setSetupRetryError(null);
      // Says what actually happened. The invitation is gated on READY and on
      // the operator having asked for one, so claiming an email was sent is
      // untrue for every tenant created with it switched off. And a tenant
      // whose product writes did not land is not reported as a success.
      if (setupFailed(data.product_setup)) {
        toast.error('Tenant created, but some products are not live in its workspace yet');
      } else {
        toast.success(form.send_invitation_when_ready
          ? 'Tenant created — the invitation is sent once provisioning is ready'
          : 'Tenant created — no invitation was sent');
      }
    } catch (err) {
      // NOT `detail` directly: a 422 makes it an ARRAY of Pydantic error
      // objects, and rendering one as a React child unmounts the whole page.
      toast.error(describeApiError(err, 'Failed to create tenant'));
    } finally {
      setCreating(false);
    }
  };

  const retryProductSetup = async () => {
    setSetupRetrying(true);
    setSetupRetryError(null);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/tenants/${success.id}/product-setup/retry`, {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
      setSuccess((prev) => ({ ...prev, product_setup: data, result: data.result }));
    } catch (err) {
      setSetupRetryError(describeApiError(err, 'The retry could not be run'));
    } finally {
      setSetupRetrying(false);
    }
  };

  // ─── Success Screen ─────────────────────────────────────────────
  if (success) {
    const invitationPresentation = creationInvitationPresentation(success);
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-lg" data-testid="tenant-create-success">
          <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
            {/* THE MOMENT A TENANT STARTS EXISTING — and Direction A says a
                major product moment carries dimensional identity artwork, not
                a flat glyph.

                WHAT WAS HERE: a 56px lucide tick on a green gradient. Both
                halves were wrong for this screen. The tick is an ACTION glyph
                — flat, monochrome, one stroke weight, the vocabulary of a
                confirm button — blown up to fourteen times the size an action
                glyph is ever drawn at; and the green gradient is not on the
                Cluvik ramp, so the one screen an operator sees at the end of
                creating a tenant was the one screen not wearing the product's
                own colours.

                The tenant silhouette at identity-2xl is the artwork the token
                file reserves for exactly this ("empty state · onboarding ·
                provisioning moment"), and it is the SAME mark the form header
                carries — the place that was about to exist, now existing. The
                tick stays, small, beside the words: the success is a fact
                about the action, and the mark is the subject. */}
            <div className="bg-gradient-to-br from-muted/60 to-card px-8 py-8 text-center border-b border-border">
              <IdentityMark
                path="/tenants/create"
                size="2xl"
                className="mx-auto mb-4"
              />
              {/* NOT "successfully" when a product write did not land: the
                  server's `result` says which, read back from the runtime. */}
              {setupFailed(success.product_setup) ? (
                <h2 className="flex items-center justify-center gap-2 text-xl font-bold text-foreground"
                    data-testid="tenant-create-incomplete">
                  <AlertTriangle className="h-5 w-5 text-warning-text shrink-0" aria-hidden="true" />
                  Tenant Created — Product Setup Incomplete
                </h2>
              ) : (
                <h2 className="flex items-center justify-center gap-2 text-xl font-bold text-foreground">
                  <CheckCircle2 className="h-5 w-5 text-success shrink-0" aria-hidden="true" />
                  Tenant Created Successfully
                </h2>
              )}
              <p className="mt-1 text-sm text-muted-foreground">
                {/* What exists NOW, in the server's own words. Provisioning is
                    a separate lifecycle and this screen does not claim it is
                    finished — the tenant record exists; the runtime build is
                    reported on Tenant Detail. */}
                The tenant record exists. Its runtime environment is built separately —
                follow it on the tenant&apos;s Provisioning tab.
              </p>
            </div>
            <div className="px-8 py-6 space-y-5">
              <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                <InfoRow label="Organization" value={success.tenant_name} />
                <InfoRow label="Plan" value={createdPlan?.name || success.plan} />
                <InfoRow label="Admin Email" value={success.admin_user?.email} />
                <InfoRow label="Admin Name" value={`${success.admin_user?.first_name} ${success.admin_user?.last_name}`} />
                <InfoRow label="Licenses" value={`${success.licenses_provisioned} provisioned`} />
                {(success.module_entitlements || []).includes('docflow') && (
                  <InfoRow label="Extra File Manager storage"
                           value={`${success.docflow_extra_storage_gb || 0} GB added to the published allowance`} />
                )}
                <InfoRow label="Modules" value={(success.module_entitlements || [])
                  .filter((c) => !internalGrants.includes(c))
                  .map((c) => productLabel(c, productLabels)).join(', ')} />
              </div>

              {/* ENVIRONMENT, INCLUDED PLATFORM SERVICES, DEMO CONNECTIONS —
                  three groups, drawn apart. Environment used to be one
                  InfoRow among seven, reading `success.environment` that the
                  service never sent, so every tenant confirmed itself as "—".
                  It now reads the stamped value, and the two things an
                  operator most often conflates (a managed service and a demo
                  connection) sit in separate boxes with the difference said. */}
              <CreatedTenantSummary success={success} />

              {/* Each product step's real outcome, with the retry beside a
                  failure. Renders nothing for a create that sold no product. */}
              <ProductSetupOutcome setup={success.product_setup}
                                   onRetry={retryProductSetup}
                                   retrying={setupRetrying}
                                   retryError={setupRetryError} />

              <div className={`rounded-xl p-4 border ${
                invitationPresentation.tone === 'sent'
                  ? 'bg-primary/5 border-primary/25'
                  : invitationPresentation.tone === 'scheduled'
                    ? 'bg-warning/10 border-warning/30'
                    : 'bg-muted/50 border-border'
              }`} data-testid="tenant-create-invitation-outcome">
                <div className="flex items-start gap-3">
                  <Send className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                    invitationPresentation.tone === 'sent'
                      ? 'text-primary'
                      : invitationPresentation.tone === 'scheduled'
                        ? 'text-warning-text'
                        : 'text-muted-foreground'
                  }`} />
                  <div>
                    <p className={`text-sm font-semibold ${
                      invitationPresentation.tone === 'sent'
                        ? 'text-primary'
                        : invitationPresentation.tone === 'scheduled'
                          ? 'text-warning-text'
                          : 'text-foreground'
                    }`}>
                      {invitationPresentation.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {invitationPresentation.detail}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Back to EMPTY_FORM, not to a shorter literal. The
                    // inlined object this replaces dropped environment,
                    // country_code, sample_credentials and both onboarding
                    // switches — every one of them a controlled input, and
                    // every one of them bound to `undefined` on the second
                    // tenant. It also re-seeded `plan` with plans[0], the
                    // auto-selection removed above for setting a tenant's
                    // enforced quotas from whichever row sorted first.
                    setSuccess(null);
                    setForm(EMPTY_FORM);
                    setCommercial({ ...EMPTY_COMMERCIAL, lines: [], product_editions: {} });
                    setDocflowSelected(false);
                    setCommercialValid(false);
                    setStarterTouched(false);
                    setProfilePayload(null);
                    setProfileError(null);
                  }}
                  className="flex-1"
                  data-testid="create-another-btn"
                >
                  Create Another
                </Button>
                <Button
                  onClick={() => navigate('/tenants')}
                  className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="back-to-tenants-btn"
                >
                  Back to Tenants
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Form ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background p-6 lg:p-8" data-testid="create-tenant-page">
      <div className="max-w-3xl mx-auto">
        {/* Header. The ONE identity moment on this screen: a tenant is a place,
            and creating one is the moment it starts existing. `/tenants/create`
            resolves to the tenant silhouette on the iris ramp — the same form
            as /tenants, a different ramp position, because it is the same area
            at a different moment. Back beside it is an ACTION and stays a flat
            monochrome glyph; no accessible name on the mark, since the h1 says
            the same thing one element away. */}
        <div className="flex flex-wrap items-start gap-3 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tenants')}
            data-testid="back-btn"
            className="mt-1"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <IdentityMark path="/tenants/create" size="lg" className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Create New Tenant</h1>
            <p className="text-sm text-muted-foreground">
              Set up a new organization — the products you select drive licenses, modules and provisioning
            </p>
          </div>
        </div>

        {/*
          Tenant provisioning is performed by the canonical Platform service.
          When it is unavailable, creation cannot succeed — and it must fail
          truthfully rather than quietly produce a half-built tenant, so the
          operator is told here instead of at the Create button.

          Gated on `required`, not on `state` alone. While the vendored path is
          still the active engine an unconfigured platform stops nothing, and a
          warning that nothing can clear is one operators learn to ignore. It
          arms itself when Platform becomes the only engine.
        */}
        {provisioningReadiness?.required && provisioningReadiness.state !== 'ready' && (
          <div
            className="mb-6 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3"
            data-testid="provisioning-unavailable"
          >
            <p className="text-sm font-semibold text-foreground">
              Tenant provisioning is unavailable
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {provisioningReadiness.detail
                || 'The tenant provisioning service is not available for this environment.'}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              This is a deployment configuration issue, not something to work
              around here.
            </p>
          </div>
        )}

        <div className="space-y-6">
          {/* Environment. Chosen first because everything else — which runtime
              provisions the tenant, which URL it logs into, whether shared
              sample credentials may be attached — follows from it. */}
          <Card
            icon={Rocket}
            title="Environment"
            subtitle="Where this tenant runs. Live is the default and cannot be changed after creation."
            testId="environment-card"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Environment">
              {ENVIRONMENT_ORDER.map((code) => {
                const entry = (environments || []).find((e) => e.code === code);
                const meta = describeEnvironment(code);
                const selected = form.environment === code;
                const attached = entry ? entry.runtime_configured : null;
                return (
                  <label
                    key={code}
                    /* `border-input`, not `border-border`. This card's edge IS the
                       control's boundary — the thing a low-vision operator uses to
                       find the option — so WCAG 2.2 SC 1.4.11 puts a 3:1 floor under
                       it. `--border` is the decorative hairline and has no such floor;
                       the two tokens used to hold the same 1.27:1 value, so this card
                       looked correct and measured wrong.

                       `focus-within` is the other half. The radio inside is a 13px
                       native control whose focus ring was the only keyboard cue on a
                       card 300px wide; a tabbing operator was hunting for it. The ring
                       is now drawn on the card, where the eye already is. */
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 bg-card cursor-pointer transition-all focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background ${
                      selected ? 'border-primary ring-2 ring-primary/20' : 'border-input hover:border-primary/40'}`}
                    data-testid={`environment-option-${code.toLowerCase()}`}
                  >
                    <input
                      type="radio"
                      name="environment"
                      value={code}
                      checked={selected}
                      onChange={() => updateField('environment', code)}
                      className="mt-1 accent-primary"
                      data-testid={`environment-radio-${code.toLowerCase()}`}
                    />
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{meta.label}</span>
                        <StatusBadge status={code.toLowerCase()} label={code} dot={false} />
                        {code === DEFAULT_ENVIRONMENT && (
                          <span className="text-xs text-muted-foreground">default</span>
                        )}
                      </span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        {entry?.description || ''}
                      </span>
                      <span
                        className={`block text-xs mt-1 ${attached ? 'text-success' : attached === false ? 'text-warning-text' : 'text-muted-foreground'}`}
                        data-testid={`environment-runtime-${code.toLowerCase()}`}
                      >
                        {attached === null
                          ? 'Runtime status unknown'
                          : attached
                            ? `Runtime attached${entry.runtime_host ? ` · ${entry.runtime_host}` : ''}`
                            : `No runtime attached — creation will be refused${
                              Array.isArray(entry?.attachment_gaps) && entry.attachment_gaps.length
                                ? ` (missing: ${entry.attachment_gaps.join(', ')})`
                                : ''
                            }`}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </Card>

          {/* Card 1: Organization Details */}
          <Card
            icon={Building2}
            title="Organization Details"
            subtitle="Basic company information"
            testId="org-details-card"
          >
            {/* ONE NAME AND THE COUNTRY, side by side. Tenant Name used to sit
                in the left slot; with it gone, Country moves up rather than
                leaving a half-empty first row and pushing every remaining field
                down a line. Same two-column rhythm, one row shorter. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Organization Name" required>
                <Input
                  value={form.organization_name}
                  onChange={(e) => updateField('organization_name', e.target.value)}
                  placeholder="Legal entity name"
                  data-testid="org-name-input"
                />
              </FormField>
              <FormField label="Country" required>
                <CountryCombobox
                  value={form.country_code}
                  onChange={(code) => updateField('country_code', code)}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Sets the tenant&apos;s default timezone, language, currency and fiscal
                  year start. All remain editable in the tenant&apos;s Setup afterwards.
                </p>
              </FormField>
            </div>
            {/* Full width, because it is now alone on its row and a street
                address in a half-width box beside empty space reads as a
                missing field rather than a wide one. */}
            <div className="mt-4">
              <FormField label="Address Line 1">
                <Input
                  value={form.address_line1}
                  onChange={(e) => updateField('address_line1', e.target.value)}
                  placeholder="Street address"
                  data-testid="address-line1-input"
                />
              </FormField>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-4">
              <FormField label="Address Line 2">
                <Input
                  value={form.address_line2}
                  onChange={(e) => updateField('address_line2', e.target.value)}
                  placeholder="Suite, floor…"
                  data-testid="address-line2-input"
                />
              </FormField>
              <FormField label="City">
                <Input
                  value={form.address_city}
                  onChange={(e) => updateField('address_city', e.target.value)}
                  data-testid="address-city-input"
                />
              </FormField>
              <FormField label="State / Region">
                <Input
                  value={form.address_state}
                  onChange={(e) => updateField('address_state', e.target.value)}
                  data-testid="address-state-input"
                />
              </FormField>
              <FormField label="Postal Code">
                <Input
                  value={form.address_postal_code}
                  onChange={(e) => updateField('address_postal_code', e.target.value)}
                  data-testid="address-postal-input"
                />
              </FormField>
            </div>
            {/* INDUSTRY IS ALWAYS ASKED, and this is a correction.

                It used to be gated on `selectedPlan.enabled_modules` including
                CRM. With the plan card removed nothing ever sets `form.plan`,
                so `selectedPlan` is always undefined and the field would have
                vanished silently — while `industry` still travels into the
                frozen provisioning specification and is hashed into it.

                The gate was also wrong before it was unreachable: CRM Console
                is COMMERCIAL_INCLUDED, so every tenant has it and the
                condition could only ever hide the field from a tenant that
                had it anyway. */}
            {(() => {
              return (
                <div className="mt-4">
                  <FormField label="Industry">
                    <select
                      value={form.industry}
                      onChange={(e) => updateField('industry', e.target.value)}
                      className="w-full h-9 rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid="industry-select"
                    >
                      <option value="">Select industry...</option>
                      {INDUSTRIES.map((i) => (
                        <option key={i} value={i.toLowerCase()}>{i}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Stored as tenant information. Every tenant receives the same
                      standard CRM baseline regardless of industry.
                    </p>
                  </FormField>
                </div>
              );
            })()}
          </Card>

          {/* Card 2: Admin User */}
          <Card
            icon={User}
            title="Tenant Administrator"
            subtitle="Admin will receive a verification email to set their password"
            testId="admin-user-card"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="First Name" required>
                <Input
                  value={form.admin_first_name}
                  onChange={(e) => updateField('admin_first_name', e.target.value)}
                  placeholder="First name"
                  data-testid="admin-first-name-input"
                />
              </FormField>
              <FormField label="Last Name" required>
                <Input
                  value={form.admin_last_name}
                  onChange={(e) => updateField('admin_last_name', e.target.value)}
                  placeholder="Last name"
                  data-testid="admin-last-name-input"
                />
              </FormField>
            </div>
            <div className="mt-4">
              <FormField label="Email Address" required>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={form.admin_email}
                    onChange={(e) => updateField('admin_email', e.target.value)}
                    placeholder="admin@company.com"
                    className="pl-10"
                    data-testid="admin-email-input"
                  />
                </div>
              </FormField>
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                <Send className="h-3 w-3" />
                A verification email will be sent to this address after creation
              </p>
            </div>
          </Card>

          {/* Card 3: what the customer is buying.
              Replaces "pick a plan and inherit whatever it says": entitlements
              now come from the products/bundle selected here, priced by the
              Commercial Core. */}
          <Card
            icon={CreditCard}
            title="Products & Pricing"
            subtitle="Select products and quantities, or a bundle. Pricing resolves from the price book."
            testId="commercial-card"
          >
            <CommercialConfigurator
              value={commercial}
              onChange={setCommercial}
              onValidityChange={onCommercialValidityChange}
            />
            {docflowSelected && (
              <div className="mt-5 rounded-2xl border border-border bg-muted/30 p-4 sm:p-5"
                   data-testid="docflow-extra-storage">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div className="max-w-xl">
                    <label htmlFor="docflow-extra-storage-gb" className="text-sm font-semibold text-foreground">
                      Extra File Manager storage for DocFlow
                    </label>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Optional. Added to this tenant's published File Manager allowance.
                      Documents use the same File Manager space as other files.
                    </p>
                  </div>
                  <div className="relative w-full sm:w-40">
                    <Input id="docflow-extra-storage-gb" type="number" min="0" max="931322" step="1"
                           inputMode="numeric" placeholder="No extra GB"
                           value={form.docflow_extra_storage_gb}
                           onChange={(e) => updateField('docflow_extra_storage_gb', e.target.value)}
                           className="pr-9" data-testid="docflow-extra-storage-input" />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">GB</span>
                  </div>
                </div>
              </div>
            )}
            {/* AFTER the configurator, and inside the same card, because the
                eligible applications ARE the commercial selection. Asked
                before the products are chosen it would offer a list about to
                change; asked in a card of its own it would look like a
                separate decision from the one that decides it. */}
            <DefaultApplicationSection
              commercial={commercial}
              value={{
                default_app_code: form.default_app_code,
                default_landing_page: form.default_landing_page,
              }}
              onChange={(next) => setForm((prev) => ({
                ...prev,
                default_app_code: next.default_app_code || '',
                // `null` is how the section says "this application has no
                // landing page"; the form holds '' for the same state, and the
                // payload omits the key entirely.
                default_landing_page: next.default_landing_page || '',
              }))}
            />
          </Card>

          {/*
            THE PLAN LABEL AND THE CONFIGURATION SUMMARY ARE GONE.

            Both were removed on the owner's instruction, repeated several
            times. What made them worth removing rather than tidying: the plan
            card rendered "QA Professional $200/mo" with its own product chips
            and "10 seats | 5 GB storage" directly beneath the real selector.
            None of those numbers described the tenant being created — seats,
            entitlements, capacity and price all come from the products above —
            so the screen showed two commercial answers and let the operator
            read the wrong one.

            A card captioned "reporting label only" does not fix that. The
            caption is read after the price.

            The summary card went with it: it existed largely to restate the
            plan and to repeat Users, which the configurator already states and
            the server already validates. One place to read the commercial
            decision, and no second opinion to disagree with it.

            `plan` is still accepted by the API for existing automation; it is
            simply no longer offered here.
          */}

          {/* Trial toggle */}
          <div className="bg-card rounded-2xl border border-border shadow-sm px-6 py-4" data-testid="trial-section">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Trial Period</p>
                <p className="text-xs text-muted-foreground">Enable a time-limited trial for this tenant</p>
              </div>
              {/* `min-h-6` — 24px, the WCAG 2.2 SC 2.5.8 floor. Measured at
                  41x20 in the browser: the native checkbox is 13px and the
                  label hugged it, so the whole target was 20px tall. It very
                  probably qualified for the spacing exception, sitting alone
                  in its card — but "probably exempt" is a worse answer than
                  24px, and 24px costs nothing here. */}
              <label className="flex min-h-6 items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_trial}
                  onChange={(e) => updateField('is_trial', e.target.checked)}
                  className="accent-primary"
                  data-testid="trial-toggle"
                />
                <span className="text-sm text-muted-foreground">{form.is_trial ? `${form.trial_days} days` : 'Off'}</span>
              </label>
            </div>
            {form.is_trial && (
              <div className="mt-3">
                <Input
                  type="number"
                  min={1}
                  max={90}
                  value={form.trial_days}
                  onChange={(e) => updateField('trial_days', parseInt(e.target.value) || 14)}
                  className="w-32"
                  data-testid="trial-days-input"
                />
              </div>
            )}
          </div>

          {/* Onboarding options. Separate from provisioning on purpose: a
              workspace being built and a person being contacted are different
              business decisions, and the tenant should be reviewable before
              anyone outside this portal hears about it. */}
          <Card
            icon={Rocket}
            title="Setup options"
            subtitle="Independent of each other. Starter content follows the environment; nothing else happens unless you ask for it."
            testId="onboarding-options-card"
          >
            <div className="space-y-3">
              {/* TWO SWITCHES, NOT ONE WITH A SUB-OPTION.
                  Sample records are rows in the tenant's own database. Demo
                  connections are shared Cluvik provider credentials pointed at
                  external accounts. Either is useful without the other — a
                  populated CRM nobody has connected an inbox to is a perfectly
                  ordinary demo, and so is an empty tenant used to exercise a
                  provider — so neither may imply the other. They are separate
                  fields on the request, separate steps in provisioning, and
                  separate controls here. */}
              {/* STARTER CONTENT (CRM Foundation decision 9). Was "Add Sample
                  Records": CRM only, OFF for every environment. Now defaulted
                  by environment, per licensed product, with the starter
                  reports stated as always added. The testid is kept: the
                  option is the same consumer contract under a better name. */}
              <div
                className="p-4 rounded-xl border-2 border-input bg-card"
                data-testid="include-sample-data-option"
              >
                <p className="mb-2 text-sm font-semibold text-foreground">Starter content</p>
                <StarterContentSection
                  environment={form.environment}
                  value={form.include_sample_data}
                  selectedModules={form.starter_content_modules}
                  licensedModules={licensedStarter}
                  onChange={(v) => updateField('include_sample_data', v)}
                  onModulesChange={(next) => updateField('starter_content_modules', next)}
                />
              </div>

              {/* INCLUDED PLATFORM SERVICES, ABOVE THE DEMO TOGGLE. Status
                  chips, not controls: there is no checkbox because there is
                  nothing to choose. Placed before Demo Connections so the
                  operator reads what every tenant already has before
                  deciding what to add — and so the two cannot be mistaken
                  for one list with some rows ticked. */}
              <div
                className="flex items-start gap-3 p-4 rounded-xl border-2 border-dashed border-border bg-muted/30"
                data-testid="managed-services-status"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-foreground">
                    Included platform services
                    <span className="ml-2 text-xs font-normal text-muted-foreground">always on</span>
                  </span>
                  <span className="mt-2 block">
                    <ManagedServicesChips testIdPrefix="form-managed-service" />
                  </span>
                  <span className="block text-xs text-muted-foreground mt-2">
                    {MANAGED_SERVICES_NOTE}
                  </span>
                </span>
              </div>

              {sampleCredentialsAllowed(form.environment) ? (
                <label
                  className="flex items-start gap-3 p-4 rounded-xl border-2 border-input hover:border-primary/40 bg-card cursor-pointer transition-all focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background"
                  data-testid="sample-credentials-option"
                >
                  <input
                    type="checkbox"
                    checked={form.sample_credentials}
                    onChange={(e) => updateField('sample_credentials', e.target.checked)}
                    className="mt-1 accent-primary"
                    data-testid="sample-credentials-toggle"
                  />
                  <span>
                    {/* "Demo Connections" — the product name, and the same
                        words the review row, the confirmation and the tenant
                        card use. The request field stays `sample_credentials`. */}
                    <span className="block text-sm font-medium text-foreground">
                      Demo Connections
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {form.sample_credentials ? 'ON' : 'OFF'}
                      </span>
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Attaches the shared {describeEnvironment(form.environment).label} demo connection
                      profile so provider integrations can be exercised end to end. A reference is
                      stored on the tenant; the credentials themselves never are.
                    </span>
                  </span>
                </label>
              ) : (
                /* Shown DISABLED rather than hidden. Hiding it leaves an
                   operator who expected the control wondering whether they
                   misremembered; saying why is one line and answers it. */
                <div
                  className="flex items-start gap-3 p-4 rounded-xl border-2 border-dashed border-border bg-muted/30"
                  data-testid="sample-credentials-unavailable"
                >
                  <input
                    type="checkbox"
                    checked={false}
                    disabled
                    readOnly
                    className="mt-1 accent-primary"
                    aria-label="Demo Connections — not available for Live"
                  />
                  <span>
                    <span className="block text-sm font-medium text-muted-foreground">
                      Demo Connections — not available for {describeEnvironment(form.environment).label}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      Shared Cluvik credentials never attach to a Live tenant. The Platform, the
                      Admin route and the profile store each refuse it, so the control is shown
                      rather than offered.
                    </span>
                  </span>
                </div>
              )}

              <label
                className="flex items-start gap-3 p-4 rounded-xl border-2 border-input hover:border-primary/40 bg-card cursor-pointer transition-all focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background"
                data-testid="send-invitation-option"
              >
                <input
                  type="checkbox"
                  checked={form.send_invitation_when_ready}
                  onChange={(e) => updateField('send_invitation_when_ready', e.target.checked)}
                  className="mt-1 accent-primary"
                  data-testid="send-invitation-toggle"
                />
                <span>
                  {/* Not "Send email". The consequence is that a person
                      outside Cluvik is contacted, and the label should say
                      so. */}
                  <span className="block text-sm font-medium text-foreground">
                    Send invitation when tenant is ready
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    Emails {form.admin_email || 'the primary administrator'} their setup
                    invitation, and only after provisioning is verified ready. Left off, you can
                    review and test the tenant first and send it yourself from Tenant Detail.
                  </span>
                </span>
              </label>
            </div>
          </Card>

          {/* The preflight. Rendered only once Demo Connections is actually
              asked for — an operator who left it off is owed a quiet screen,
              not a section explaining an absence. */}
          {demo.state !== 'off' && demo.state !== 'not_allowed' && (
            <DemoConnectionsReview
              demo={demo}
              onRefresh={() => loadProfiles(form.environment)}
            />
          )}

          {/* What will actually happen. No hidden side effects. */}
          <div
            className="bg-muted/40 rounded-2xl border border-border px-6 py-5"
            data-testid="create-tenant-review"
          >
            <p className="text-sm font-semibold text-foreground mb-3">On create</p>
            <dl className="space-y-2.5 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Environment</dt>
                <dd className="text-foreground text-right" data-testid="review-environment">
                  {describeEnvironment(form.environment).label}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Demo Connections</dt>
                {/* Counted from the profile that was actually read, not from a
                    constant list in this repo. The constant said "5
                    connections" whatever the runtime held — including when
                    the runtime held nothing, or could not be reached. */}
                <dd className="text-foreground text-right" data-testid="review-sample-credentials">
                  {!sampleCredentialsAllowed(form.environment) ? 'Not available for Live'
                    : !form.sample_credentials ? 'Off'
                      : demo.severity === 'unknown' ? 'On · readiness unknown'
                        : demo.severity === 'blocked' ? 'On · nothing can be attached'
                          : `On · ${demo.counts?.total ?? 0} connection${demo.counts?.total === 1 ? '' : 's'}, ${summarizeReadiness(demo.counts)}`}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Provisioning</dt>
                {/* This said "Canonical Platform provisioning" unconditionally,
                    while the default deployment runs the vendored path. The
                    page cannot see which one will run, so it no longer claims
                    to. */}
                <dd className="text-foreground text-right">Standard tenant provisioning</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Starter content</dt>
                {/* testid unchanged: the label is the operator's word for it,
                    the hook is a consumer contract, and renaming both at once
                    breaks anything selecting on it for no gain. */}
                <dd className="text-foreground text-right" data-testid="review-sample-data">
                  {describeStarterContentChoice(form, licensedStarter)}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Initial access</dt>
                <dd className="text-foreground text-right" data-testid="review-invitation">
                  {form.send_invitation_when_ready
                    ? 'Invitation after readiness'
                    : 'No invitation sent'}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Temporary password</dt>
                <dd className="text-foreground text-right">Not generated</dd>
              </div>
            </dl>
            {form.send_invitation_when_ready && (
              <p className="mt-3 text-xs text-muted-foreground">
                Held if provisioning fails, if verification does not pass, or if requested
                sample data fails — you can review and send it manually.
              </p>
            )}
          </div>

          {/* Submit */}
          {blockingReason && (
            <div
              className="rounded-md border border-border bg-warning/10 px-3 py-2 text-sm text-warning-text"
              data-testid="create-tenant-blocked-reason"
              role="status"
            >
              {blockingReason}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2 pb-8">
            <Button
              variant="outline"
              onClick={() => navigate('/tenants')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || creating}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 disabled:opacity-50"
              data-testid="create-tenant-submit"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Tenant'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Reusable sub-components ──────────────────────────────────────

// The <label> used to be a sibling with no `htmlFor`, so no input on this page
// had a programmatic name: a screen reader announced "edit text" (or the
// placeholder) for Organization Name, Email Address and the rest, and "*" was
// the only sign a field was required (T29/NX-23, S18 browser evidence). The
// label now points at its control - the first element under it that takes a
// value - which receives the id and aria-required. A control that already
// carries an id keeps it.
const isFormControl = (el) => React.isValidElement(el)
  && ('onChange' in el.props || 'onValueChange' in el.props || 'value' in el.props || 'checked' in el.props);

const wireControl = (node, controlId, required) => {
  if (!React.isValidElement(node)) return { node, id: null };
  if (isFormControl(node)) {
    const id = node.props.id || controlId;
    return { node: React.cloneElement(node, { id, ...(required ? { 'aria-required': true } : {}) }), id };
  }
  // Descend through plain wrappers (an icon + input in a relative <div>).
  if (typeof node.type === 'string' && node.props.children) {
    let id = null;
    const kids = React.Children.map(node.props.children, (child) => {
      if (id) return child;
      const wired = wireControl(child, controlId, required);
      id = wired.id;
      return wired.node;
    });
    return { node: React.cloneElement(node, undefined, kids), id };
  }
  return { node, id: null };
};

const FormField = ({ label, required, children }) => {
  const generatedId = React.useId();
  let id = null;
  const wiredChildren = React.Children.map(children, (child) => {
    if (id) return child;
    const wired = wireControl(child, generatedId, required);
    id = wired.id;
    return wired.node;
  });
  return (
    <div>
      <label htmlFor={id || undefined} className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
        {label} {required && <span aria-hidden="true" className="text-destructive-text">*</span>}
      </label>
      {wiredChildren}
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-foreground font-medium">{value || '—'}</span>
  </div>
);


export default CreateTenantPage;
